import { FunctionComponent } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import EmptyStateAction, { EmptyAction } from './EmptyStateAction'
import { Info, Calendar } from 'react-feather'
import colors from '../../colors'
import { SPACING } from '../../constants'
import { UpcomingRenewal } from '../../types'

type UpcomingRenewalsCardProps = {
  emptyAction?: EmptyAction,
  renewals: UpcomingRenewal[],
  loading: boolean,
}

const UpcomingRenewalsCard: FunctionComponent<UpcomingRenewalsCardProps> = ({ renewals, loading, emptyAction }) => {
  const styles = useStyles()

  const InfoIcon: FunctionComponent = () => {
    return (
      <Box className={styles.infoContainer}>
        <Tooltip title="Subscriptions renewing in the next 30 days">
          <Info size={16} strokeWidth={3} color={colors.GREY_3} />
        </Tooltip>
      </Box>
    )
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleContainer}>
        <Calendar size={20} color={colors.GREY_3} style={{ marginRight: SPACING }} />
        <Typography variant="h6">
          Upcoming Renewals
        </Typography>
        <InfoIcon />
      </Box>

      {loading ? (
        <Box mt={2}>
          <Skeleton count={5} height={40} />
        </Box>
      ) : renewals.length === 0 ? (
        <Box className={styles.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No renewals scheduled.
          </Typography>
          <EmptyStateAction action={emptyAction} />
        </Box>
      ) : (
        <Box className={styles.tableScroll}>
  <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={styles.tableHeader}>Name</TableCell>
                <TableCell className={styles.tableHeader}>Email</TableCell>
                <TableCell className={styles.tableHeader}>Membership</TableCell>
                <TableCell className={styles.tableHeader}>Renewal Date</TableCell>
                <TableCell className={styles.tableHeader} align="right">MRR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renewals.map((renewal) => (
                <TableRow key={renewal.id} hover className={styles.tableRow}>
                  <TableCell>{renewal.patronName || '-'}</TableCell>
                  <TableCell className={styles.emailCell}>{renewal.patronEmail || '-'}</TableCell>
                  <TableCell>
                    <Box className={styles.membershipBadge}>
                      {renewal.membershipTitle}
                    </Box>
                  </TableCell>
                  <TableCell>{renewal.renewalDate}</TableCell>
                  <TableCell align="right" className={styles.mrrCell}>{renewal.mrr}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  // The card must never widen the page: a table has an intrinsic minimum
  // width, and in a half-width column it exceeded the card and pushed the
  // whole dashboard into horizontal scroll.
  tableScroll: {
    width: '100%',
    overflowX: 'auto',
  },
  container: {
    borderRadius: 12,
    boxShadow: '0px 0px 4px rgba(17, 51, 85, 0.15)',
    padding: SPACING * 3,
    backgroundColor: '#fff',
    width: '100%',
    minWidth: 0,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: SPACING * 2,
  },
  infoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    cursor: 'pointer',
  },
  tableHeader: {
    fontWeight: 600,
    color: colors.GREY_3,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  emailCell: {
    color: colors.GREY_3,
    fontSize: '0.875rem',
  },
  membershipBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 12,
    backgroundColor: '#fff3e0',
    color: '#ef6c00',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  mrrCell: {
    fontWeight: 600,
    color: '#2e7d32',
  },
  emptyState: {
    padding: SPACING * 4,
    display: 'flex',
    // Column, so an empty-state action sits under the message rather than
    // running on from the end of the sentence.
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    textAlign: 'center',
  },
})

export default UpcomingRenewalsCard
